import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppyLogo from "./AppyLogo";

const links = [
  { to: "/stays", label: "Stays" },
  { to: "/stays?managed=true", label: "Managed" },
  { to: "/events", label: "Events" },
  { to: "/partner", label: "Partner" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 site-header ${scrolled ? "scrolled py-3.5" : "py-5"}`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo">
          <AppyLogo size={48} />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              className={({ isActive }) =>
                `font-sora text-[0.95rem] font-medium transition-colors ${
                  isActive ? "text-[#0E2E24]" : "text-[#5B6B63] hover:text-[#0E2E24]"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/partner")}
            className="font-sora text-sm font-medium text-[#0E2E24] hover:bg-[#E8F2EC] rounded-full px-5"
            data-testid="nav-list-property-btn"
          >
            List Property
          </Button>
          <Button
            onClick={() => navigate("/stays")}
            className="bg-[#0E2E24] hover:bg-[#1F5A43] text-[#FAFAF8] rounded-full px-6 py-5 font-sora font-medium text-sm gap-2"
            data-testid="nav-book-now-btn"
          >
            Book Now <ArrowRight size={15} />
          </Button>
        </div>

        <button
          className="lg:hidden p-2 text-[#0E2E24]"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden px-6 pb-6 pt-3 space-y-4 bg-[#FAFAF8] border-t border-[#0E2E24]/10">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block font-sora text-base font-medium text-[#0E2E24]"
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => { setOpen(false); navigate("/partner"); }}
              variant="outline"
              className="rounded-full border-[#0E2E24]/20 flex-1 font-sora"
              data-testid="nav-mobile-list-property"
            >
              List Property
            </Button>
            <Button
              onClick={() => { setOpen(false); navigate("/stays"); }}
              className="bg-[#0E2E24] text-[#FAFAF8] rounded-full flex-1 font-sora gap-2"
              data-testid="nav-mobile-book-now"
            >
              Book Now <ArrowRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
